import React, {useEffect, useState} from 'react';
import {
    Box, Typography, TextField, MenuItem,
    InputLabel, Grid2
} from "@mui/material";
import {calculateSeverancePay} from './severanceCalculator';
import {getGSPay} from './gsPayTable';
import './styles.css';

export default function SeveranceCalculatorForm() {
    const [formInput, setFormInput] = useState({
        annualSalary: '',
        yearsOfService: '',
        ageYears: '',
        ageMonths: '',
    });
    const [result, setResult] = useState(null);
    const [errors, setErrors] = useState({});
    const [grade, setGrade] = useState('');
    const [step, setStep] = useState('');

    // Sync GS Grade/Step with Annual Salary
    const handleGradeStepChange = (gradeValue, stepValue) => {
        if (gradeValue && stepValue) {
            const pay = getGSPay(parseInt(gradeValue), parseInt(stepValue));
            setFormInput((prev) => ({...prev, annualSalary: pay ?? ''})); // Sync with formInput
        }
    };

    // Universal Change Handler for All Inputs
    const handleChange = (e) => {
        const {name, value} = e.target;

        const error = validate(name, value);
        setErrors((prev) => ({...prev, [name]: error}));

        setFormInput((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    // Form Validation
    const validate = (name, value) => {
        let error = '';
        if (name === 'annualSalary' && (value < 0 || isNaN(value))) {
            error = 'Annual Salary must be a positive number.';
        }
        if (name === 'yearsOfService' && (value < 0 || value > 50)) {
            error = 'Years of Service must be between 0 and 50.';
        }
        if (name === 'ageYears' && (value < 0 || value > 64)) {
            error = 'Age in Years must be between 0 and 64.';
        }
        if (name === 'ageMonths' && (value < 0 || value > 11)) {
            error = 'Months must be between 0 and 11.';
        }
        return error;
    };

    // Auto-Calculate Severance When Inputs Change
    useEffect(() => {
        const newErrors = {};
        Object.entries(formInput).forEach(([key, value]) => {
            const error = validate(key, value);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            const {annualSalary, yearsOfService, ageYears, ageMonths} = formInput;
            if (annualSalary && yearsOfService && ageYears !== '') {
                const calc = calculateSeverancePay({
                    annualSalary: parseFloat(annualSalary),
                    yearsOfService: parseInt(yearsOfService),
                    ageYears: parseInt(ageYears),
                    ageMonths: ageMonths === '' ? 0 : parseInt(ageMonths),
                });
                setResult(calc);
            } else {
                setResult(null);
            }
        }
    }, [formInput]);


    return (
        <Box sx={{p: 0}}>
            <Typography variant="h4" gutterBottom>
                Reduction in Force (RIF) Calculator
            </Typography>
            <Typography variant="h6" gutterBottom>
                How much is my estimated severance pay?
            </Typography>

            {/**************ANNUAL SALARY INPUT****************/}
            <Box component='fieldset' className='form-section'>
                <Typography component="legend">Enter your Grade & Step:</Typography>
                <Box sx={{display: 'flex', gap: 2, mb: 2}}>
                        <TextField
                            select
                            label="GS Grade *"
                            value={grade}
                            onChange={(e) => {
                                setGrade(e.target.value);
                                handleGradeStepChange(e.target.value, step);
                            }}
                            fullWidth
                        >
                            {[...Array(15).keys()].map((g) => (
                                <MenuItem key={g + 1} value={g + 1}>
                                    GS {g + 1}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Step *"
                            value={step}
                            onChange={(e) => {
                                setStep(e.target.value);
                                handleGradeStepChange(grade, e.target.value);
                            }}
                            fullWidth
                        >
                            {[...Array(10).keys()].map((s) => (
                                <MenuItem key={s + 1} value={s + 1}>
                                    Step {s + 1}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                <Grid2 container sx={{mb: 2, border: 0}}>
                    <Grid2 xs={6} sx={{mr: 2}}>
                        <TextField
                            label="Basic Annual Salary"
                            name="annualSalary"
                            value={formInput.annualSalary}
                            type="number"
                            required
                            fullWidth
                            error={!!errors.annualSalary}
                            helperText={errors.annualSalary}
                            InputProps={{readOnly: true}}
                            disabled
                        />
                    </Grid2>
                    {/* <Grid2 xs={6}>
                        <FormControlLabel
                            control={<Checkbox checked={manualEntry} onChange={handleManualToggle}/>}
                            label="Enter Pay Manually"
                        />
                    </Grid2> */}
                </Grid2>
            </Box>

            {/**************SERVICE YEARS INPUT****************/}
            <Box component='fieldset' className='form-section'>
                <Typography component="legend">How many years of service do you have?</Typography>
                <TextField
                    label="Years of Service"
                    name="yearsOfService"
                    required
                    type="number"
                    value={formInput.yearsOfService}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.yearsOfService}
                    helperText={errors.yearsOfService}
                    sx={{mb: 2}}
                    inputProps={{ min: 1, max: 50 }}
                />
            </Box>

            {/**************AGE INPUT****************/}
            <Box component='fieldset' className='form-section'>
                <Typography component="legend">How old are you?</Typography>
                <Grid2
                    container
                    spacing={2}
                    justifyContent="center"
                    direction={{xs: 'column', sm: 'row'}}  // Stack vertically on xs, horizontal on sm+
                >
                    {/* Age in Years */}
                    <Grid2 item xs={12} sm={4}>
                        <TextField
                            label="Age (Years)"
                            name="ageYears"
                            type="number"
                            required
                            value={formInput.ageYears}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.ageYears}
                            helperText="16–64 years"
                        />
                    </Grid2>
                    <Grid2 item xs={12} sm="auto" sx={{textAlign: 'center'}}>
                        <InputLabel sx={{mt: {xs: 1, sm: 0}, fontWeight: 'bold'}}>and</InputLabel>
                    </Grid2>
                    {/* Age in Months */}
                    <Grid2 item xs={12} sm={4}>
                        <TextField
                            label="(Months)"
                            name="ageMonths"
                            type="number"
                            value={formInput.ageMonths}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.ageMonths}
                            helperText="0–11 months"
                            placeholder="0"
                            inputProps={{ min: 0, max: 11 }}
                        />
                    </Grid2>
                </Grid2>
            </Box>


            {/* Output Section */}
            {!!result && (
                <>
                    <Box component='fieldset' className='form-section'>
                        <Typography component="legend">Results:</Typography>

                        <Typography variant="h6">Estimated Severance Pay Fund:</Typography>
                        <Typography variant="body1">
                            ${result.estimatedTotalSeverancePayFund || 0}
                        </Typography>

                        <Typography variant="h6">Bi-Weekly Pay:</Typography>
                        <Typography variant="body1">
                            ${result.biweeklySeverancePayment || 0}
                        </Typography>

                        <Typography variant="h6">Severance Duration (Weeks):</Typography>
                        <Typography variant="body1">
                            {result.numberWeeksSeverance || 0} weeks
                        </Typography>
                    </Box>
                </>
            )}

        </Box>
    );
}
